import { api } from "@/lib/api";

const SKIP = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "CODE", "INPUT", "SELECT"]);
const ATTRS = ["placeholder", "title", "aria-label", "alt"];
const LETTER = /\p{L}/u;
const BATCH = 150;
const textRec = new WeakMap();
const attrRec = new WeakMap();
const dicts = {};
const inflight = new Set();
let lang = "it";
let observer = null;
let timer = null;
let onBusy = () => {};

const dict = (l) => {
  if (!dicts[l]) {
    try { dicts[l] = JSON.parse(localStorage.getItem(`cs_tr_${l}`) || "{}"); } catch { dicts[l] = {}; }
  }
  return dicts[l];
};
const save = (l) => { try { localStorage.setItem(`cs_tr_${l}`, JSON.stringify(dicts[l])); } catch { /* quota */ } };
const blocked = (el) => !el || SKIP.has(el.tagName) || el.isContentEditable || !!el.closest("[data-no-translate]");

const NUM = /\d+(?:[.,]\d+)*/g;
const translateValue = (value, d, missing) => {
  const key = value.trim();
  if (!key || !LETTER.test(key)) return null;
  const nums = key.match(NUM) || [];
  let i = 0;
  const tpl = key.replace(NUM, () => `{${i++}}`);
  if (d[tpl] == null) { missing.add(tpl); return null; }
  return value.replace(key, d[tpl].replace(/\{(\d+)\}/g, (m, j) => nums[j] ?? m));
};

function scan(missing) {
  const d = dict(lang);
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const rec = textRec.get(n);
    if ((rec && n.nodeValue === rec.out) || blocked(n.parentElement)) continue;
    const out = translateValue(n.nodeValue, d, missing);
    if (out != null) { textRec.set(n, { src: n.nodeValue, out }); n.nodeValue = out; }
  }
  document.body.querySelectorAll(ATTRS.map((a) => `[${a}]`).join(",")).forEach((el) => {
    if (el.closest("[data-no-translate]")) return;
    const recs = attrRec.get(el) || {};
    ATTRS.forEach((a) => {
      const v = el.getAttribute(a);
      if (!v || (recs[a] && recs[a].out === v)) return;
      const out = translateValue(v, d, missing);
      if (out != null) { recs[a] = { src: v, out }; el.setAttribute(a, out); }
    });
    attrRec.set(el, recs);
  });
}

async function fetchMissing(missing) {
  const l = lang;
  const todo = [...missing].filter((t) => !inflight.has(`${l}|${t}`));
  if (!todo.length) return;
  todo.forEach((t) => inflight.add(`${l}|${t}`));
  onBusy(true);
  try {
    const chunks = [];
    for (let i = 0; i < todo.length; i += BATCH) chunks.push(todo.slice(i, i + BATCH));
    const res = await Promise.allSettled(chunks.map((texts) => api.post("/translate", { lang: l, texts })));
    res.forEach((r) => r.status === "fulfilled" && Object.assign(dict(l), r.value.data.translations));
    save(l);
  } finally {
    todo.forEach((t) => inflight.delete(`${l}|${t}`));
    onBusy(false);
  }
  if (l === lang) run();
}

function run() {
  if (lang === "it") return;
  const missing = new Set();
  scan(missing);
  if (missing.size) fetchMissing(missing);
}

function restore() {
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const rec = textRec.get(n);
    if (rec && n.nodeValue === rec.out) n.nodeValue = rec.src;
    textRec.delete(n);
  }
  document.body.querySelectorAll(ATTRS.map((a) => `[${a}]`).join(",")).forEach((el) => {
    const recs = attrRec.get(el) || {};
    Object.entries(recs).forEach(([a, r]) => el.getAttribute(a) === r.out && el.setAttribute(a, r.src));
    attrRec.delete(el);
  });
}

const preloaded = {};
const preload = (l) => {
  preloaded[l] = preloaded[l] || api.get(`/translations/${l}`).then((r) => {
    Object.assign(dict(l), r.data);
    save(l);
  }).catch(() => {});
  return preloaded[l];
};

export function setTranslatorBusyListener(fn) { onBusy = fn; }

export async function setSiteLanguage(next) {
  restore();
  lang = next;
  document.documentElement.lang = next;
  observer?.disconnect();
  observer = null;
  if (next === "it") return;
  if (!Object.keys(dict(next)).length) onBusy(true);
  await preload(next);
  onBusy(false);
  if (lang !== next) return;
  observer = new MutationObserver(() => { clearTimeout(timer); timer = setTimeout(run, 120); });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  run();
}
